import React, { createContext, useContext, useState } from 'react';

export type Lang = 'nl' | 'en';

// ── Translations ──────────────────────────────────────────────────────────────
const T = {
  nl: {
    nav: { dashboard: 'Dashboard', reports: 'Rapportage', onboarding: 'Onboarding', billing: 'Facturatie', logout: 'Afmelden' },
    lang: { label: 'Taal', nl: 'Nederlands', en: 'English' },
    status: {
      green: 'Voltooid', red: 'Afgewezen', expired: 'Geen reactie',
      pending: 'In behandeling', out_of_credits: 'Geen verificaties',
      approved: 'Voltooid', denied: 'Afgewezen',
    },
    dashboard: {
      title: 'Dashboard', subtitle: 'Laatste 30 dagen',
      totalVerifications: 'Totaal verificaties', successPct: 'Geslaagd %',
      rejected: 'Afgewezen', credits: 'Verificaties resterend',
      verificationsPerDay: 'Verificaties per dag', statusDistribution: 'Status verdeling',
      recentVerifications: 'Recente verificaties',
      loadError: 'Kon dashboardgegevens niet laden',
      noVerifications: 'Geen recente verificaties gevonden.',
      verifications: 'Verificaties',
      cols: { name: 'Naam', company: 'Bedrijf', contact: 'Contact', status: 'Status', document: 'Document', date: 'Datum' },
      notSet: 'Niet ingesteld',
    },
    reports: {
      title: 'Rapportage', subtitle: 'Automatische verzending & handmatige download',
      autoSend: {
        title: 'Automatische verzending',
        description: 'Ontvang automatisch een periodiek PDF-rapport per e-mail met een samenvatting van alle verificaties.',
        email: 'E-mailadres', emailPlaceholder: 'naam@bedrijf.be', interval: 'Interval',
        intervals: { '7d': 'Elke 7 dagen', '14d': 'Elke 14 dagen', '30d': 'Elke maand' } as Record<string, string>,
        save: 'Opslaan', saved: '✓ Instellingen opgeslagen',
      },
      download: {
        title: 'Rapport downloaden',
        description: 'Selecteer een datumbereik en formaat. CSV is direct beschikbaar; PDF duurt ±30 seconden.',
        period: 'Periode',
        format: 'Formaat', formatPdf: 'PDF', formatCsv: 'CSV',
        generate: 'Genereer rapport',
        generating: 'Verificaties ophalen...', generatingDesc: 'Data wordt geladen en rapport aangemaakt',
        polling: 'PDF wordt gegenereerd...', pollingDesc: 'Dit duurt ±30 seconden. De download start automatisch.',
        complete: 'Rapport klaar!', completeDesc: 'De download is automatisch gestart.',
        downloadAgain: 'Opnieuw downloaden', newReport: 'Nieuw rapport',
        failed: 'Generatie mislukt', retry: 'Opnieuw proberen',
      },
      detail: {
        title: 'Detail rapport opvragen',
        description: 'Geef een verificatie-ID (scanRef) in en download het detailrapport van iDenfy voor die specifieke verificatie.',
        scanRefLabel: 'Verificatie ID',
        scanRefPlaceholder: 'bv. 350e2420-8850-11e9-baa5-309c231b1bac',
        fetchBtn: 'Vraag detail rapport op',
        fetching: 'Detail rapport ophalen...',
        complete: 'Rapport klaar! De download is gestart.',
        notFound: 'Verificatie niet gevonden of behoort niet tot dit account.',
        failed: 'Kon detail rapport niet ophalen.',
      },
    },
    onboarding: {
      title: 'Onboarding mails',
      subtitle: 'Genereer klant-mails in uw eigen huisstijl & download of kopieer ze',
      company: {
        title: 'Uw bedrijf',
        companyLabel: 'Bedrijfsnaam', companyPlaceholder: 'Uw Bedrijf BV',
        websiteLabel: 'Website', websitePlaceholder: 'https://www.uwbedrijf.be',
        emailLabel: 'Support e-mail', emailPlaceholder: 'support@uwbedrijf.be',
        phoneLabel: 'Telefoon', phoneOptional: '(optioneel)', phonePlaceholder: '+32 ...',
        logoLabel: 'Logo', logoHint: '(PNG of JPG)',
        logoDropMain: 'Klik om te uploaden', logoDropAux: 'of sleep hierheen',
        logoUploaded: 'Geüpload', logoRemove: 'Verwijder',
      },
      brand: {
        title: 'Uw huisstijl',
        primaryLabel: 'Primaire merkkleur',
        paletteAria: 'Voorgestelde kleuren',
        fontLabel: 'Lettertype',
        toneLabel: 'Tone of voice', toneFormal: 'U-vorm (formeel)', toneCasual: 'Je-vorm (casual)',
      },
      details: {
        title: 'Details',
        serviceLabel: 'Naam van de service', serviceHint: '(zoals klant het ziet)',
        servicePlaceholder: 'beveiligde identiteitscontrole',
        goliveLabel: 'Go-live datum', golivePlaceholder: 'maandag 16 juni 2026',
        priceToggle: 'Prijs vermelden in de mails',
        priceLabel: 'Prijs per verificatie', priceHint: '(verkoopprijs)',
        reset: 'Alles wissen & opnieuw beginnen',
        resetConfirm: 'Alle gegevens wissen en opnieuw beginnen?',
      },
      preview: {
        announceNum: '01 · Aankondiging', announceTitle: 'We voeren ID-verificatie in',
        howNum:      '02 · Uitleg',       howTitle:      'Zo werkt het — in 30 sec',
        goliveNum:   '03 · Go-live',      goliveTitle:   'Morgen wordt het actief',
        subjectLabel: 'Onderwerp:',
        copySubject: 'Onderwerp', copyHtml: 'Kopieer HTML', download: 'Download',
        iframeTitle: 'E-mail preview',
        help: 'Tip: kopieer de HTML en plak ze in uw e-mailtool (Outlook, Gmail, Brevo, Mailchimp…). Alles blijft lokaal in uw browser.',
      },
      toast: {
        copied: 'Gekopieerd!',
        htmlCopied: 'HTML gekopieerd!',
        subjectCopied: 'Onderwerp gekopieerd!',
        downloaded: 'Gedownload!',
        copyFailed: 'Kon niet kopiëren',
        notAnImage: 'Kies een afbeelding',
        logoTooBig: 'Logo te groot (max 1 MB)',
      },
      fileSlugs: { announce: '01-aankondiging', how: '02-zo-werkt-het', golive: '03-go-live' },
    },
    billing: {
      title: 'Facturatie', subtitle: 'Beheer uw abonnement en verificaties',
      loading: 'Laden...', loadError: 'Kon de facturatiegegevens niet laden. Probeer het opnieuw.',
      status: {
        trialing: 'Trial', active: 'Actief', past_due: 'Betaling achterstallig',
        canceled: 'Opgezegd', incomplete: 'Onvolledig',
      },
      currentPlan: {
        title: 'Uw abonnement',
        trialNote: 'U gebruikt momenteel de gratis trial met 10 verificaties.',
        creditsRemaining: 'Verificaties resterend',
        planCredits: 'Maandelijkse verificaties',
        autoTopup: 'Auto-aanvulling',
        autoTopupCredits: 'verificaties per aanvulling',
        manageBtn: 'Facturen & betaalgegevens beheren',
      },
      plans: {
        title: 'Kies uw pakket',
        subtitle: 'Pakketten worden maandelijks gefactureerd. Overschakelen is mogelijk en gaat in na de huidige betaalperiode.',
        currentBadge: 'Huidig pakket',
        subscribeBtn: 'Kiezen',
        switchBtn: 'Overschakelen',
        perMonth: '/maand',
        verificationsPerMonth: 'verificaties per maand',
      },
      modal: {
        title: 'Abonnement afsluiten',
        autoTopupTitle: 'Automatisch bijkopen',
        autoTopupDesc: 'Wanneer uw verificaties op zijn, worden er automatisch extra verificaties bijgekocht aan €4 per verificatie. U kiest nu het aantal per aanvulling.',
        qtyLabel: 'Verificaties per aanvulling',
        qtyMin: 'Minimum 10 verificaties',
        consentLabel: 'Ik ga akkoord met het automatisch bijkopen van verificaties aan €4/verificatie wanneer mijn saldo op is.',
        proceedBtn: 'Doorgaan naar betaling',
        cancelBtn: 'Annuleren',
        consentRequired: 'Bevestig uw akkoord met de auto-aanvulling om verder te gaan.',
      },
      policy: {
        bannerTitle: 'Privacyverklaring tekenen',
        bannerDesc: 'Als onderdeel van uw abonnement dient u de privacyverklaring te lezen en te ondertekenen. Dit duurt slechts een minuut.',
        viewBtn: 'Bekijk privacyverklaring',
        signBtn: 'Ik ga akkoord en onderteken',
        signed: '✓ Privacyverklaring ondertekend',
        signing: 'Bezig...',
      },
      portal: {
        title: 'Stripe Klantportaal',
        description: 'Bekijk en download facturen, wijzig betaalgegevens of uw bedrijfsgegevens.',
        openBtn: 'Openen',
        noAccount: 'Geen facturatieaccount gevonden.',
      },
      tabs: {
        subscription: 'Abonnement',
        company:      'Bedrijfsgegevens',
        invoices:     'Facturen',
        payment:      'Betaalmethode',
      },
      company: {
        title: 'Bedrijfsgegevens',
        subtitle: 'Deze gegevens worden gebruikt op uw facturen en doorgegeven aan Stripe.',
        companyName: 'Bedrijfsnaam', vatNumber: 'BTW-nummer',
        vatHint: 'Bijv. BE0123456789',
        address: 'Adres (straat + nummer)', city: 'Gemeente', zip: 'Postcode', country: 'Land',
        save: 'Opslaan', saved: '✓ Opgeslagen', saving: 'Opslaan...',
        saveError: 'Opslaan mislukt. Controleer uw gegevens.',
        loadError: 'Kon bedrijfsgegevens niet laden.',
        noSubscription: 'Bedrijfsgegevens worden opgeslagen na het afsluiten van een abonnement.',
      },
      invoices: {
        title: 'Facturen',
        subtitle: 'Overzicht van alle facturen. Klik op de downloadknop om een PDF te openen.',
        noInvoices: 'Nog geen facturen beschikbaar.',
        loadError: 'Kon facturen niet laden.',
        colDate: 'Datum', colNumber: 'Factuurnummer', colPeriod: 'Periode',
        colAmount: 'Bedrag', colStatus: 'Status', colActions: '',
        download: 'PDF', open: 'Bekijk',
        statusPaid: 'Betaald', statusOpen: 'Openstaand', statusVoid: 'Geannuleerd', statusDraft: 'Concept',
        noSubscription: 'Facturen zijn beschikbaar zodra u een abonnement heeft.',
      },
      payment: {
        title: 'Betaalmethode',
        subtitle: 'Beheer de kaart die gebruikt wordt voor uw maandelijkse abonnement.',
        current: 'Huidige kaart',
        noMethod: 'Geen betaalmethode ingesteld.',
        expires: 'Geldig tot',
        updateBtn: 'Kaart bijwerken',
        cancelBtn: 'Annuleren',
        saveBtn: 'Opslaan',
        saving: 'Verwerken...',
        saved: '✓ Betaalmethode bijgewerkt',
        saveError: 'Bijwerken mislukt. Probeer opnieuw.',
        loadError: 'Kon betaalmethode niet laden.',
        noSubscription: 'Betaalmethode is beschikbaar zodra u een abonnement heeft.',
        cardLabel: 'Kaartgegevens',
        cardHint: 'Kaartgegevens worden versleuteld verwerkt door Stripe.',
      },
    },
    auth: { loginFailed: 'Aanmelden mislukt', retry: 'Opnieuw proberen', loggingIn: 'Aanmelden...' },
    uninstallBanner: {
      title: 'XyloTrust is gedeïnstalleerd',
      bodyDays: 'Je gegevens worden binnen {days} dagen verwijderd conform onze voorwaarden.',
      bodyToday: 'Je gegevens worden vandaag verwijderd conform onze voorwaarden.',
      cta: 'Installeer de app opnieuw om dit te voorkomen.',
      reinstallLink: 'Installatie-instructies',
    },
  },

  en: {
    nav: { dashboard: 'Dashboard', reports: 'Reporting', onboarding: 'Onboarding', billing: 'Billing', logout: 'Sign out' },
    lang: { label: 'Language', nl: 'Dutch', en: 'English' },
    status: {
      green: 'Completed', red: 'Rejected', expired: 'No response',
      pending: 'Pending', out_of_credits: 'No verifications',
      approved: 'Completed', denied: 'Rejected',
    },
    dashboard: {
      title: 'Dashboard', subtitle: 'Last 30 days',
      totalVerifications: 'Total verifications', successPct: 'Success %',
      rejected: 'Rejected', credits: 'Verifications remaining',
      verificationsPerDay: 'Verifications per day', statusDistribution: 'Status distribution',
      recentVerifications: 'Recent verifications',
      loadError: 'Failed to load dashboard data',
      noVerifications: 'No recent verifications found.',
      verifications: 'Verifications',
      cols: { name: 'Name', company: 'Company', contact: 'Contact', status: 'Status', document: 'Document', date: 'Date' },
      notSet: 'Not set',
    },
    reports: {
      title: 'Reporting', subtitle: 'Automatic delivery & manual download',
      autoSend: {
        title: 'Automatic delivery',
        description: 'Automatically receive a periodic PDF report by email with a summary of all verifications.',
        email: 'Email address', emailPlaceholder: 'name@company.com', interval: 'Interval',
        intervals: { '7d': 'Every 7 days', '14d': 'Every 14 days', '30d': 'Monthly' } as Record<string, string>,
        save: 'Save', saved: '✓ Settings saved',
      },
      download: {
        title: 'Download report',
        description: 'Select a date range and format. CSV is available immediately; PDF takes ±30 seconds.',
        period: 'Period',
        format: 'Format', formatPdf: 'PDF', formatCsv: 'CSV',
        generate: 'Generate report',
        generating: 'Fetching verifications...', generatingDesc: 'Loading data and creating report',
        polling: 'Generating PDF...', pollingDesc: 'This takes ±30 seconds. The download will start automatically.',
        complete: 'Report ready!', completeDesc: 'The download has started automatically.',
        downloadAgain: 'Download again', newReport: 'New report',
        failed: 'Generation failed', retry: 'Try again',
      },
      detail: {
        title: 'Request detail report',
        description: 'Enter a verification ID (scanRef) and download the iDenfy detail report for that specific verification.',
        scanRefLabel: 'Verification ID',
        scanRefPlaceholder: 'e.g. 350e2420-8850-11e9-baa5-309c231b1bac',
        fetchBtn: 'Request detail report',
        fetching: 'Fetching detail report...',
        complete: 'Report ready! The download has started.',
        notFound: 'Verification not found or does not belong to this account.',
        failed: 'Could not fetch detail report.',
      },
    },
    onboarding: {
      title: 'Onboarding emails',
      subtitle: 'Generate customer emails in your own brand style & download or copy them',
      company: {
        title: 'Your company',
        companyLabel: 'Company name', companyPlaceholder: 'Your Company Ltd',
        websiteLabel: 'Website', websitePlaceholder: 'https://www.yourcompany.com',
        emailLabel: 'Support email', emailPlaceholder: 'support@yourcompany.com',
        phoneLabel: 'Phone', phoneOptional: '(optional)', phonePlaceholder: '+1 ...',
        logoLabel: 'Logo', logoHint: '(PNG or JPG)',
        logoDropMain: 'Click to upload', logoDropAux: 'or drag here',
        logoUploaded: 'Uploaded', logoRemove: 'Remove',
      },
      brand: {
        title: 'Your brand identity',
        primaryLabel: 'Primary brand colour',
        paletteAria: 'Suggested colours',
        fontLabel: 'Font',
        toneLabel: 'Tone of voice', toneFormal: 'Formal', toneCasual: 'Casual',
      },
      details: {
        title: 'Details',
        serviceLabel: 'Service name', serviceHint: '(as the customer sees it)',
        servicePlaceholder: 'secure identity verification',
        goliveLabel: 'Go-live date', golivePlaceholder: 'Monday 16 June 2026',
        priceToggle: 'Mention price in the emails',
        priceLabel: 'Price per verification', priceHint: '(sell price)',
        reset: 'Clear everything & start over',
        resetConfirm: 'Clear all data and start over?',
      },
      preview: {
        announceNum: '01 · Announcement', announceTitle: "We're introducing ID verification",
        howNum:      '02 · How it works', howTitle:      'How it works — in 30 sec',
        goliveNum:   '03 · Go-live',      goliveTitle:   'It goes live tomorrow',
        subjectLabel: 'Subject:',
        copySubject: 'Subject', copyHtml: 'Copy HTML', download: 'Download',
        iframeTitle: 'Email preview',
        help: 'Tip: copy the HTML and paste it into your email tool (Outlook, Gmail, Brevo, Mailchimp…). Everything stays local in your browser.',
      },
      toast: {
        copied: 'Copied!',
        htmlCopied: 'HTML copied!',
        subjectCopied: 'Subject copied!',
        downloaded: 'Downloaded!',
        copyFailed: 'Could not copy',
        notAnImage: 'Choose an image',
        logoTooBig: 'Logo too large (max 1 MB)',
      },
      fileSlugs: { announce: '01-announcement', how: '02-how-it-works', golive: '03-go-live' },
    },
    billing: {
      title: 'Billing', subtitle: 'Manage your subscription and verifications',
      loading: 'Loading...', loadError: 'Failed to load billing details. Please try again.',
      status: {
        trialing: 'Trial', active: 'Active', past_due: 'Payment overdue',
        canceled: 'Canceled', incomplete: 'Incomplete',
      },
      currentPlan: {
        title: 'Your subscription',
        trialNote: 'You are currently on the free trial with 10 verifications.',
        creditsRemaining: 'Verifications remaining',
        planCredits: 'Monthly verifications',
        autoTopup: 'Auto top-up',
        autoTopupCredits: 'verifications per top-up',
        manageBtn: 'Manage invoices & payment details',
      },
      plans: {
        title: 'Choose your plan',
        subtitle: 'Plans are billed monthly. You can switch plans at any time; changes take effect at the next billing cycle.',
        currentBadge: 'Current plan',
        subscribeBtn: 'Get started',
        switchBtn: 'Switch plan',
        perMonth: '/month',
        verificationsPerMonth: 'verifications per month',
      },
      modal: {
        title: 'Start subscription',
        autoTopupTitle: 'Automatic top-up',
        autoTopupDesc: 'When your verifications run out, additional verifications are automatically purchased at €4 per verification. Choose the quantity per top-up.',
        qtyLabel: 'Verifications per top-up',
        qtyMin: 'Minimum 10 verifications',
        consentLabel: 'I agree to the automatic purchase of verifications at €4/verification when my balance runs out.',
        proceedBtn: 'Proceed to payment',
        cancelBtn: 'Cancel',
        consentRequired: 'Please confirm your agreement to the auto top-up to proceed.',
      },
      policy: {
        bannerTitle: 'Sign the privacy policy',
        bannerDesc: 'As part of your subscription, you need to read and sign the privacy policy. It only takes a minute.',
        viewBtn: 'View privacy policy',
        signBtn: 'I agree and sign',
        signed: '✓ Privacy policy signed',
        signing: 'Processing...',
      },
      portal: {
        title: 'Stripe Customer Portal',
        description: 'View and download invoices, update payment details or company information.',
        openBtn: 'Open',
        noAccount: 'No billing account found.',
      },
      tabs: {
        subscription: 'Subscription',
        company:      'Company details',
        invoices:     'Invoices',
        payment:      'Payment method',
      },
      company: {
        title: 'Company details',
        subtitle: 'This information appears on your invoices and is passed to Stripe.',
        companyName: 'Company name', vatNumber: 'VAT number',
        vatHint: 'E.g. BE0123456789',
        address: 'Address (street + number)', city: 'City', zip: 'Postal code', country: 'Country',
        save: 'Save', saved: '✓ Saved', saving: 'Saving...',
        saveError: 'Failed to save. Please check your details.',
        loadError: 'Failed to load company details.',
        noSubscription: 'Company details can be saved once you have an active subscription.',
      },
      invoices: {
        title: 'Invoices',
        subtitle: 'All invoices for your account. Click the download button to open the PDF.',
        noInvoices: 'No invoices yet.',
        loadError: 'Failed to load invoices.',
        colDate: 'Date', colNumber: 'Invoice number', colPeriod: 'Period',
        colAmount: 'Amount', colStatus: 'Status', colActions: '',
        download: 'PDF', open: 'View',
        statusPaid: 'Paid', statusOpen: 'Open', statusVoid: 'Void', statusDraft: 'Draft',
        noSubscription: 'Invoices will be available once you have a subscription.',
      },
      payment: {
        title: 'Payment method',
        subtitle: 'Manage the card used for your monthly subscription.',
        current: 'Current card',
        noMethod: 'No payment method on file.',
        expires: 'Expires',
        updateBtn: 'Update card',
        cancelBtn: 'Cancel',
        saveBtn: 'Save',
        saving: 'Processing...',
        saved: '✓ Payment method updated',
        saveError: 'Update failed. Please try again.',
        loadError: 'Failed to load payment method.',
        noSubscription: 'Payment method is available once you have a subscription.',
        cardLabel: 'Card details',
        cardHint: 'Card details are encrypted and processed by Stripe.',
      },
    },
    auth: { loginFailed: 'Login failed', retry: 'Try again', loggingIn: 'Signing in...' },
    uninstallBanner: {
      title: 'XyloTrust has been uninstalled',
      bodyDays: 'Your data will be deleted within {days} days in accordance with our terms.',
      bodyToday: 'Your data will be deleted today in accordance with our terms.',
      cta: 'Reinstall the app to prevent this.',
      reinstallLink: 'Installation instructions',
    },
  },
} as const;

export type Translations = typeof T[Lang];

// ── Context ───────────────────────────────────────────────────────────────────
interface LangCtx { lang: Lang; setLang: (l: Lang) => void; t: Translations; }
const Ctx = createContext<LangCtx>({ lang: 'nl', setLang: () => {}, t: T.nl });

function getInitialLang(): Lang {
  return localStorage.getItem('portal_lang') === 'en' ? 'en' : 'nl';
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);
  const setLang = (l: Lang) => { localStorage.setItem('portal_lang', l); setLangState(l); };
  return <Ctx.Provider value={{ lang, setLang, t: T[lang] }}>{children}</Ctx.Provider>;
}

export function useLang() { return useContext(Ctx); }
